import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { UserTwoIcon, WarningIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { createAffectedStaff } from '../../../api/affected-staffs/create-affected-staff'
import { getIdDocumentWarnings } from '../../../api/affected-staffs/get-id-document-warnings'
import {
  getAffectedStaffPayscale,
  getAffectedStaffPayscaleQueryKey
} from '../../../api/affected-staffs/get-payscale'
import { getStaffRequest } from '../../../api/staff-requests/get-staff-request'
import useTranslate from '../../../lib/hooks/use-translate'
import { missionStatusEnumMapping } from '../../../lib/types/enums/mission/mission-status.enum'
import { StaffPositionEnum } from '../../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffRequestInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { Staff } from '../../../lib/types/models/staff/staff'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getAffectedStaffRemuneration } from '../../../lib/utils/functions/affected-staff-remuneration.functions'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { ItemNotFound } from '../../elements/item-not-found'
import AffectStaffForm from '../affected-staffs/affect-staff-form/affect-staff-form'
import IdDocumentWarningsModal from '../affected-staffs/id-document-warnings-modal'
import AffectedStaffCenterDetailsBadge from './affected-staff-center-details-badge'
import AssignStaff from './assign-staff'
import { mapStaffRequestToAffectedStaffInput } from './utils'

type Props = {
  className?: string
  staffRequestId: number
  toggleModal: () => void
  onSave: () => void
}

const AffectStaff = ({ className, staffRequestId, toggleModal, onSave }: Props) => {
  const [step, setStep] = useState(0)
  const [staff, setStaff] = useState<Staff>()
  // Affectation waiting for the identity document warnings to be acknowledged
  const [pendingAffectation, setPendingAffectation] = useState<{
    input: AffectedStaffRequestInput
    warnings: string[]
  }>()
  // Affectation waiting for the 0 € remuneration to be confirmed
  const [zeroRemunerationAffectation, setZeroRemunerationAffectation] =
    useState<AffectedStaffRequestInput>()
  const staffId = staff?.id

  const { t } = useTranslate()

  const { data, isPending } = useQuery({
    queryKey: ['staff-request', staffRequestId],
    queryFn: () => getStaffRequest(staffRequestId)
  })

  const createAffectedStaffMutation = useMutation({
    mutationFn: createAffectedStaff,
    onSuccess: () => {
      setStep(0)
      setPendingAffectation(undefined)
      onSave()
      toast.success('Staff affecté avec succès')
    },
    onError: showAxiosError
  })

  const idDocumentWarningsMutation = useMutation({
    mutationFn: getIdDocumentWarnings,
    onError: showAxiosError
  })

  // Same query as the "Rémunération" block, so the check uses the total shown to the user
  const { data: payScale, isPending: isPayScalePending } = useQuery({
    queryKey: getAffectedStaffPayscaleQueryKey(staffId, staffRequestId),
    queryFn: () => {
      if (staffId) return getAffectedStaffPayscale(staffId, staffRequestId)
    },
    enabled: !!staffId
  })

  const affect = (input: AffectedStaffRequestInput) =>
    idDocumentWarningsMutation.mutate(
      { staffId: input.staffId, staffRequestId, endDate: input.endDate },
      {
        onSuccess: ({ warnings }) => {
          if (warnings.length) setPendingAffectation({ input, warnings })
          else createAffectedStaffMutation.mutate(input)
        }
      }
    )

  const affectedStaffs = data?.affectedStaffs.map((affectedStaff) => affectedStaff.staffId)

  if (isPending) return <Loader center />
  if (!data) return <ItemNotFound title="Demande de staff non trouvée" />

  return (
    <div className={cn('flex flex-col gap-4 ', className)}>
      {step === 1 && staff && (
        <Typography variant="h6">
          {`Encadrant : ${toFullName(staff.firstName, staff.lastName)}`}
        </Typography>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Box className="bg-white-hover border border-grey-light rounded flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <UserTwoIcon color={tailwindColors.black.DEFAULT} />
            <Badge
              text={missionStatusEnumMapping[data.status].label}
              variant={missionStatusEnumMapping[data.status].variant}
            />
            <Badge
              text={`Qualification : ${t(staffQualificationEnumMapping[data.qualification].label)}`}
              variant="primary"
            />
          </div>
          <div className="flex items-center gap-1">
            <Typography variant="caption" color={tailwindColors.grey.DEFAULT}>
              {`# Demandés : ${data.numberOfStaff} | ${data.affectedStaffs.length} Affectés : `}
            </Typography>
            <div className="flex items-center gap-1 flex-wrap">
              {data.affectedStaffs.length
                ? data.affectedStaffs.map(({ id, staff }) => (
                    <Badge key={id} text={toFullName(staff.firstName, staff.lastName)} />
                  ))
                : '-'}
            </div>
          </div>
        </Box>
        {data.rad && (
          <Box className="bg-white-hover border border-grey-light rounded w-fit flex flex-col gap-2.5">
            <Typography variant="caption" color={tailwindColors.grey.DEFAULT}>
              Réunion avant départ
            </Typography>

            <Badge text={CenterProperties.getRadNameForRequestStaff(data.rad)} variant="primary" />
          </Box>
        )}
      </div>
      {data.centers && data.centers.length > 0 && (
        <>
          <Box className="bg-white-hover border border-grey-light rounded  flex flex-col gap-2">
            <Typography variant="caption" color={tailwindColors.grey.DEFAULT}>
              Infos du séjour
            </Typography>
            {data.centers.map((center) => (
              <AffectedStaffCenterDetailsBadge key={center.id} center={center} />
            ))}
          </Box>
        </>
      )}
      {step === 0 && (
        <AssignStaff
          filters={{
            excludedStaffIds: affectedStaffs,
            qualifications: [data.qualification],
            position: [StaffPositionEnum.ANIMATOR, StaffPositionEnum.TEACHER],
            deactivated: false,
            availableForStaffRequestId: staffRequestId
          }}
          staffId={staffId}
          onStaffSelect={setStaff}
          onSave={() => {
            if (!staffId) return
            setStep(1)
          }}
          onCancel={() => toggleModal()}
        />
      )}
      {step === 1 && staffId && (
        <AffectStaffForm
          defaultValues={mapStaffRequestToAffectedStaffInput(data)}
          staffRequestId={staffRequestId}
          staffId={staffId}
          buttonsConfig={modalButtonConfig({
            onConfirm: (value) => {
              if (!staffId) return
              const input = { ...value, staffId, staffRequestId }
              // A 0 € remuneration never blocks the affectation, it only asks for a confirmation
              if (getAffectedStaffRemuneration(value, payScale).total === 0)
                setZeroRemunerationAffectation(input)
              else affect(input)
            },
            confirmText: 'Assigner',
            onCancel: () => setStep(step - 1),
            cancelText: 'Retour',
            // The 0 € check needs the pay scale, so "Assigner" waits for it
            isLoading:
              isPayScalePending ||
              createAffectedStaffMutation.isPending ||
              idDocumentWarningsMutation.isPending
          })}
        />
      )}
      <Modal
        onCancel={() => setZeroRemunerationAffectation(undefined)}
        isOpen={!!zeroRemunerationAffectation}
        title="Attention"
        icon={WarningIcon}
        variant="confirmation"
        actionButtonsOptions={{
          confirmText: 'Assigner quand même',
          cancelText: 'Annuler',
          onConfirm: () => {
            if (zeroRemunerationAffectation) affect(zeroRemunerationAffectation)
            setZeroRemunerationAffectation(undefined)
          }
        }}
      >
        <Typography variant="body-medium" color={tailwindColors.black.DEFAULT}>
          Êtes-vous sûr de vouloir assigner cet encadrant avec une rémunération à 0 € ?
        </Typography>
      </Modal>
      <IdDocumentWarningsModal
        isOpen={!!pendingAffectation}
        warnings={pendingAffectation?.warnings ?? []}
        isLoading={createAffectedStaffMutation.isPending}
        onCancel={() => setPendingAffectation(undefined)}
        onConfirm={() => {
          if (pendingAffectation) createAffectedStaffMutation.mutate(pendingAffectation.input)
        }}
      />
    </div>
  )
}

export default AffectStaff
